import { faCheck, faPlus, faUserSecret } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { ERouteStaticParams, Routes } from '../../classes/Routes';
import Alert from '../../components/UI/Alert/Alert';
import Button, { EButtonSize } from '../../components/UI/Button/Button';
import Container from '../../components/UI/Container/Container';
import Spinner from '../../components/UI/Spinner/Spinner';
import Table from '../../components/UI/Table/Table';
import TableTh from '../../components/UI/Table/TableTh/TableTh';
import { useTableSort } from '../../components/UI/Table/useTableSort';
import { IAppState, IUser, IUserState } from '../../interfaces';
import { linkHandler } from '../../shared/utility';
import * as actions from '../../store/actions';
import { ETranslation } from '../../translations/translation-keys';

interface IProps extends RouteComponentProps {
  organizationId?: string;
}

const Users: React.FC<IProps> = ({ history, organizationId }) => {
  const dispatch = useDispatch();

  const authUser = useSelector((state: IAppState) => state.auth.authUser);
  const { loading, error, users } = useSelector<IAppState, IUserState>(state => state.users);
  const { sort, onSort, items: sortedUsers } = useTableSort({ data: users });

  const { t } = useTranslation();

  useEffect(() => {
    organizationId
      ? dispatch(actions.listUsers(organizationId))
      : dispatch(actions.listUsers());
  }, [dispatch, organizationId]);

  const handleImpersonateUserRow = (
    event: React.MouseEvent,
    targetUser: IUser
  ) => {
    event.stopPropagation();
    const orgLevel = targetUser.organization?.level || "SERVICE_PROVIDER";
    const orgIdNum = targetUser.organization?.id
      ? +targetUser.organization.id
      : organizationId
      ? +organizationId
      : undefined;
    dispatch(
      actions.startImpersonation({
        userId: +targetUser.id,
        userName: targetUser.name,
        orgId: orgIdNum,
        orgName: targetUser.organization?.name,
        level: orgLevel,
        allowWrite: false,
        subscriptionLevel: targetUser.organization?.subscriptionLevel || "BASIC",
      })
    );
    history.push("/");
  };

  const showUser = (
    event: React.MouseEvent<HTMLTableRowElement>,
    id: string
  ) => {
    if (organizationId) {
      linkHandler(event, Routes.ORGANIZATION_USER(organizationId, id), history);
    } else {
      linkHandler(event, Routes.USER(id), history);
    }
  };

  const addUser = () => {
    if (organizationId) {
      history.push(
        Routes.ORGANIZATION_USER(organizationId, ERouteStaticParams.new)
      );
    } else {
      history.push(Routes.USER(ERouteStaticParams.new));
    }
  };

  return (
    <Container
      style={organizationId ? { padding: 0, margin: 0, marginTop: "1rem" } : { maxWidth: 1200, margin: '1rem auto' }}
    >
      {error && <Alert>{t(error)}</Alert>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0 }}>{t(ETranslation.TITLE_USERS)}</h2>
        <Button onClick={addUser} icon={faPlus}>
          {t(ETranslation.USERS_ADD_NEW)}
        </Button>
      </div>
      {loading ? (
        <Spinner />
      ) : users && sortedUsers ? (
        <Table hover sortContext={{ sort, onSort }}>
          <thead>
            <tr>
              <TableTh sort="name">{t(ETranslation.USER_NAME)}</TableTh>
              <TableTh sort="email">{t(ETranslation.USER_EMAIL)}</TableTh>
              <TableTh sort="title">{t(ETranslation.USER_TITLE)}</TableTh>
              <TableTh sort="phoneNumber">{t(ETranslation.USER_PHONE_NUMBER)}</TableTh>
              <TableTh sort="streetAddress">{t(ETranslation.USER_ADDRESS)}</TableTh>
              <TableTh sort="allowLogin" type="boolean">{t(ETranslation.USER_ALLOW_LOGIN)}</TableTh>
              {authUser?.isPrimary && <th style={{ width: '130px', textAlign: 'center' }}></th>}
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((user) => (
              <tr key={user.id} onClick={(e) => showUser(e, user.id)}>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>{user.title}</td>
                <td>{user.phoneNumber}</td>
                <td>
                  {user.streetAddress} {user.zip} {user.city}
                </td>
                <td>
                  {user.allowLogin ? <FontAwesomeIcon icon={faCheck} /> : ""}
                </td>
                {authUser?.isPrimary && (
                  <td 
                    style={{ textAlign: 'center' }} 
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      size={EButtonSize.SMALL}
                      icon={faUserSecret}
                      title={t(ETranslation.IMPERSONATE_ACTION_USER)}
                      onClick={(e: React.MouseEvent) => handleImpersonateUserRow(e, user)}
                      style={{
                        backgroundColor: "#f59e0b",
                        color: "#fff",
                        borderColor: "#d97706",
                        padding: "0.2rem 0.5rem",
                        fontSize: "0.75rem",
                      }}
                    >
                      {t(ETranslation.IMPERSONATE_ACTION_USER)}
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p>{t(ETranslation.NO_USERS)}</p>
      )}
    </Container>
  );
};

export default Users;
